import {
  FREE_PET_SLOTS,
  type EquippedPetSlot,
  type UserCharacterState,
  type UserGlobalState,
  type UserPetState,
  type UserUpgradeState,
} from './types';

/**
 * 유저의 성장/로드아웃 스펙 — prod↔staging 복사 대상 필드만 담는다.
 * 재화($asset)·진행도(stage/tutorial)·랭킹은 포함하지 않는다.
 */
export interface UserSpec {
  characters: UserCharacterState[];
  upgrades: UserUpgradeState[];
  pets: UserPetState[];
  ownedSkins: { characterId: number; skinId: number }[];
  selectedCharacterId: number;
  equippedPetSlots: EquippedPetSlot[];
  petSlotCount: number;
  questPetSlotCount: number;
  equippedSkins: Record<number, number | null>;
}

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** UserGlobalState 에서 스펙 필드만 추출. null 은 기본값 스펙. */
export function pickUserSpec(state: UserGlobalState | null): UserSpec {
  return {
    characters: state?.characters ?? [],
    upgrades: state?.upgrades ?? [],
    pets: state?.pets ?? [],
    ownedSkins: state?.ownedSkins ?? [],
    selectedCharacterId: state?.selectedCharacterId ?? 0,
    equippedPetSlots: state?.equippedPetSlots ?? [],
    // 코드베이스 관례(`petSlotCount ?? FREE_PET_SLOTS`)와 parity — 레거시 undefined
    // 유저가 유효 슬롯 2 인데 0 으로 export/import 되어 어긋나는 것 방지.
    petSlotCount: state?.petSlotCount ?? FREE_PET_SLOTS,
    questPetSlotCount: state?.questPetSlotCount ?? 0,
    equippedSkins: state?.equippedSkins ?? {},
  };
}

function validCharacters(v: unknown): v is UserCharacterState[] {
  return Array.isArray(v) && v.every((c) => isObj(c) && isNum(c.characterId));
}
function validUpgrades(v: unknown): v is UserUpgradeState[] {
  return (
    Array.isArray(v) &&
    v.every((u) => isObj(u) && isNum(u.characterId) && isNum(u.upgradeId) && isNum(u.level))
  );
}
function validPets(v: unknown): v is UserPetState[] {
  return Array.isArray(v) && v.every((p) => isObj(p) && isNum(p.petId) && isNum(p.level));
}
function validOwnedSkins(v: unknown): v is { characterId: number; skinId: number }[] {
  return Array.isArray(v) && v.every((s) => isObj(s) && isNum(s.characterId) && isNum(s.skinId));
}
function validEquippedPetSlots(v: unknown): v is EquippedPetSlot[] {
  return Array.isArray(v) && v.every((s) => isObj(s) && isNum(s.petId) && isNum(s.slotNumber));
}
function validEquippedSkins(v: unknown): v is Record<number, number | null> {
  // 키는 characterId(JSON 에서 문자열로 도착) — 숫자로 파싱 가능해야 하고,
  // 값은 skinId(number) 또는 null.
  return (
    isObj(v) &&
    Object.entries(v).every(([k, x]) => Number.isInteger(Number(k)) && (x === null || isNum(x)))
  );
}

/**
 * 형태만 검증한다. 마스터데이터 id 존재 여부는 대조하지 않는다.
 * 알 수 없는 최상위 키는 drop, 누락 필드는 기본값으로 채운다.
 */
export function validateUserSpec(
  json: unknown
): { ok: true; spec: UserSpec } | { ok: false; error: string } {
  if (!isObj(json)) return { ok: false, error: 'spec must be an object' };
  const spec = pickUserSpec(null);
  if ('characters' in json) {
    if (!validCharacters(json.characters)) return { ok: false, error: 'invalid characters' };
    spec.characters = json.characters;
  }
  if ('upgrades' in json) {
    if (!validUpgrades(json.upgrades)) return { ok: false, error: 'invalid upgrades' };
    spec.upgrades = json.upgrades;
  }
  if ('pets' in json) {
    if (!validPets(json.pets)) return { ok: false, error: 'invalid pets' };
    spec.pets = json.pets;
  }
  if ('ownedSkins' in json) {
    if (!validOwnedSkins(json.ownedSkins)) return { ok: false, error: 'invalid ownedSkins' };
    spec.ownedSkins = json.ownedSkins;
  }
  if ('selectedCharacterId' in json) {
    if (!isNum(json.selectedCharacterId))
      return { ok: false, error: 'invalid selectedCharacterId' };
    spec.selectedCharacterId = json.selectedCharacterId;
  }
  if ('equippedPetSlots' in json) {
    if (!validEquippedPetSlots(json.equippedPetSlots))
      return { ok: false, error: 'invalid equippedPetSlots' };
    spec.equippedPetSlots = json.equippedPetSlots;
  }
  if ('petSlotCount' in json) {
    if (!isNum(json.petSlotCount)) return { ok: false, error: 'invalid petSlotCount' };
    spec.petSlotCount = json.petSlotCount;
  }
  if ('questPetSlotCount' in json) {
    if (!isNum(json.questPetSlotCount)) return { ok: false, error: 'invalid questPetSlotCount' };
    spec.questPetSlotCount = json.questPetSlotCount;
  }
  if ('equippedSkins' in json) {
    if (!validEquippedSkins(json.equippedSkins))
      return { ok: false, error: 'invalid equippedSkins' };
    spec.equippedSkins = json.equippedSkins as Record<number, number | null>;
  }
  return { ok: true, spec };
}
